import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/navigation';

import { LaunchpoolStatus } from '@flipster/apis/v1/launchpool/types';
import SegmentClick from '@flipster/features/src/misc/segment/segment-click';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { useV1Currencies } from '@flipster/features/src/shared/currency/use-v1-currencies';
import { launchpoolItemBunja } from '@flipster/features/src/shared/launchpool/state';
import { useOverlay } from '@flipster/overlay';
import { CountdownDate } from '@flipster/react/design-utils/countdown-date';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import ShieldCheckIcon from '@flipster/react/icon/generated/ShieldCheckIcon';
import SparklesIcon from '@flipster/react/icon/generated/SparklesIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive/index';
import { useLocalStorage } from '@flipster/react/misc/state/use-local-storage';
import { css, cx } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { alert } from '@flipster/utils/alert';
import { DateFormatter, isSameUTCYear } from '@flipster/utils/datetime';
import Num, { numberFormatter } from '@flipster/utils/num';
import { StakeEvent } from '@flipster/utils/segment/events/stake';

import { useWalletModal } from '@web/app/[locale]/(web)/_modal/WalletModal/hooks/useWalletModal';
import {
  currencyCssRaw,
  dividerCssRaw,
  dlCssRaw,
  layoutCssRaw,
} from '@web/app/[locale]/(web)/launchpool/_lists/_Item/shared-css';

import StakingModal from '../../_modals/staking-modal';
import { getVIPEstimatedApr } from '../utils';
import StakingStatusBadge from './LaunchpoolStatusBadge';

const LaunchpoolInfo = () => {
  const router = useRouter();
  const overlay = useOverlay();

  const { isLoggedIn, user } = useAuth();
  const { isMobile } = useBreakpoint();

  const { launchpoolItemAtom } = useBunja(launchpoolItemBunja);
  const launchpool = useAtomValue(launchpoolItemAtom);
  const { currenciesKeyByCurrency } = useV1Currencies();
  const { openWalletModal } = useWalletModal();
  const [isSkipHedgingNotice] = useLocalStorage({
    key: `flipster.configs.v1.${user?.userId}.skipHedgingNoticeConfirm`,
    defaultValue: false,
  });

  const {
    id,
    stakingCurrency,
    rewardCurrency,
    status,
    totalReward,
    startAt,
    endAt,
    estimatedApr,
    firstDepositorBannerStatus,
    totalStaked,
    stakedAmount,
    vipMultiplier,
    multipliers,
    showHedge,
    minVipTierForMultiplier,
  } = launchpool;

  const VIPEstimatedApr = getVIPEstimatedApr(estimatedApr, vipMultiplier);
  const isSameYear = isSameUTCYear(startAt, endAt);
  const isClosed = status === LaunchpoolStatus.Closed;
  const isUpcoming = status === LaunchpoolStatus.Upcoming;
  const isOngoing = status === LaunchpoolStatus.Ongoing;
  const rewardCurrencyPrecision = currenciesKeyByCurrency?.[rewardCurrency]?.blockchainInfo[0]?.withdrawalPrecision;
  const stakingCurrencyPrecision = currenciesKeyByCurrency?.[stakingCurrency]?.blockchainInfo[0]?.withdrawalPrecision;
  const firstDepositMultiplier = multipliers.find((tvm) => tvm.multiplierType === 'MULTIPLIER_TYPE_FIRST_DEPOSIT');

  const firstDepositorApr =
    estimatedApr &&
    firstDepositMultiplier?.multiplier &&
    Num(estimatedApr).multipliedBy(firstDepositMultiplier.multiplier);
  const isShowFirstDepositBanner = firstDepositorBannerStatus !== 'FIRST_DEPOSITOR_BANNER_STATUS_NO_BANNER';
  const isNotFirstDepositedStatus = firstDepositorBannerStatus === 'FIRST_DEPOSITOR_BANNER_STATUS_NOT_DEPOSITED_BANNER';
  const isShowHedgeBanner = showHedge && !isMobile && !isClosed && Num(stakedAmount).isGreaterThan(0);

  const onClickAprInfoButton = () => {
    alert.show({
      title: 'Estimated APR',
      message: (
        <div>
          {'The rate on the page is for reference only.\n\nThe Annual Percentage Rate (APR) depends on:'}
          <ul>
            <li>{'{Daily distributed rewards / sum(Multiplier * staked amounts) across users} * 365'}</li>
            <li>Historical data calculated at the most recent 00:00 UTC.</li>
          </ul>
          <br />
          Higher total staked tokens may reduce the projected APR.
        </div>
      ),
      confirmText: 'OK',
    });
  };

  const onClickDepositBanner = () => {
    if (firstDepositorBannerStatus === 'FIRST_DEPOSITOR_BANNER_STATUS_FIRST_DEPOSITED_BANNER') return;
    if (!isLoggedIn) {
      router.push('/login');
      return;
    }
    openWalletModal('deposit');
  };

  const onClickHedgingBanner = () => {
    overlay.open((close) => (
      <StakingModal launchpoolId={id} onClose={close} step={isSkipHedgingNotice ? 'hedging' : 'hedgingNotice'} />
    ));
  };

  return (
    <div className={cx(css(layoutCssRaw, { flex: 1 }), isClosed && 'closed')}>
      <dl className={css(dlCssRaw)}>
        <div>
          <dt>Total rewards</dt>
          <dd>
            <Formatted.Number
              value={totalReward}
              suffix={<span className={css(currencyCssRaw)}>{rewardCurrency}</span>}
              precision={rewardCurrencyPrecision}
              zeroString='-'
            />
          </dd>
        </div>
        <div>
          <dt>Staking period</dt>
          <dd>
            <Datetime value={startAt} format={DateFormatter.ShortYearDateTime} isUTC />
            <br />
            {'- '}
            <Datetime
              value={endAt}
              format={isSameYear ? DateFormatter.MonthDateTime : DateFormatter.ShortYearDateTime}
              isUTC
              withUTCText
            />
          </dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>
            <StakingStatusBadge status={status} />
            {(isUpcoming || isOngoing) && (
              <CountdownDate
                value={isUpcoming ? startAt : endAt}
                format='${dd}D ${hh}H ${mm}M ${ss}S left'
                css={{ color: 'content.default.level-2', mt: 4 }}
              />
            )}
          </dd>
        </div>
        <div className={css(dividerCssRaw)} />
        <div>
          <dt className={hstack()}>
            <span>Estimated APR</span>
            <button type='button' onClick={onClickAprInfoButton} className={css({ buttonShape: 'inline-icon', ml: 4 })}>
              <InformationCircleIcon size={16} color={token('colors.content.default.level-1')} />
            </button>
          </dt>
          <dd>
            <Formatted.Percent
              value={estimatedApr}
              css={{
                textStyle: 'Title/XS',
                color: isClosed ? 'content.default.level-2' : 'content.state.positive',
              }}
            />
            <div>
              {`VIP ${minVipTierForMultiplier}+ get `}
              <Formatted.Percent value={VIPEstimatedApr} />
            </div>
          </dd>
        </div>
        {isShowFirstDepositBanner && (
          <button
            type='button'
            onClick={onClickDepositBanner}
            className={hstack({
              w: '100%',
              justify: 'space-between',
              gap: 8,
              py: 8,
              px: 12,
              my: 8,
              bgColor: 'background.default.level-2',
              rounded: 4,
            })}
          >
            <SparklesIcon size={16} className={css({ color: 'content.accent.promotion' })} />
            <div className={css({ flex: 1, textAlign: 'left' })}>
              <p className={css({ textStyle: 'Text/S/Strong', color: 'content.default.level-4' })}>
                {isNotFirstDepositedStatus && 'Deposit and get '}
                {!isNotFirstDepositedStatus && 'Stake to activate '}
                {numberFormatter(Number(firstDepositorApr), { withComma: true, precision: 2 })}% APR (
                {firstDepositMultiplier?.multiplier}x)
              </p>
              <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-3' })}>
                {isNotFirstDepositedStatus && 'For new users only, on your first deposit'}
                {!isNotFirstDepositedStatus && 'Will be automatically applied when you stake'}
              </p>
            </div>
            {isNotFirstDepositedStatus && (
              <ChevronRightIcon size={16} color={token('colors.content.default.level-2')} />
            )}
          </button>
        )}
        <div className={css(dividerCssRaw)} />
        <div>
          <dt>Total staked</dt>
          <dd>
            <Formatted.Number
              value={totalStaked}
              suffix={<span className={css(currencyCssRaw)}>{stakingCurrency}</span>}
              precision={stakingCurrencyPrecision}
              zeroString='-'
            />
          </dd>
        </div>
        <div>
          <dt>You staked</dt>
          <dd>
            <Formatted.Number
              value={stakedAmount}
              suffix={<span className={css(currencyCssRaw)}>{stakingCurrency}</span>}
              precision={stakingCurrencyPrecision}
            />
          </dd>
        </div>
        {isShowHedgeBanner && (
          <SegmentClick
            event={StakeEvent.StakeHedgeClick}
            data={{ currency: stakingCurrency, event_location: 'earn_launchpool_tab' }}
          >
            <button
              type='button'
              onClick={onClickHedgingBanner}
              className={hstack({
                justifyContent: 'space-between',
                px: 12,
                py: 8,
                mt: 8,
                w: '100%',
                rounded: 4,
                bgColor: 'background.default.level-2',
              })}
            >
              <p
                className={css({
                  display: 'inline-flex',
                  textStyle: 'Text/S/Strong',
                  color: 'content.default.level-3',
                  gap: 8,
                })}
              >
                <ShieldCheckIcon size={16} className={css({ color: 'content.default.level-4' })} /> Hedge to protect
                your stake
              </p>
              <ChevronRightIcon size={16} className={css({ color: 'content.default.level-2' })} />
            </button>
          </SegmentClick>
        )}
      </dl>
    </div>
  );
};

export default LaunchpoolInfo;
